import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getNotifications } from 'src/api/notifications';
import { useAuthContext } from 'src/auth/hooks';
import { HOST_API } from 'src/config-global';
import { Notification } from 'src/models/notification';
import { User } from 'src/models/user';
import { ModelUpdateData /* RealtimeUpdateHandler */ } from 'src/utils/realtime-update-handler';
import { NotificationContext } from './notification-context-types';

interface NotificationProviderProps {
  children: React.ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuthContext();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);

  const handleRealtimeUpdates = useCallback((data: ModelUpdateData, currentUser: User) => {
    if (data.user === currentUser?._id) {
      return;
    }
    switch (data.type) {
      // case "Progress":
      //   RealtimeUpdateHandler.handleProgress(data, progressContext)
      //   return
      default:
        // Unknown model update — warn instead of throwing so the socket
        // handler doesn't crash on unexpected event types.
        console.warn(`[notification-context] no realtime handler for '${data.type}'`);
    }
  }, []);

  const handleRealTimeUpdateRef = useRef(handleRealtimeUpdates);

  useEffect(() => {
    if (!user) return () => null;

    const fetchNotifications = async () => {
      const res = await getNotifications();
      if (res.success) {
        setNotifications((prev) => {
          const incoming = res.data as Notification[];
          const seen = new Set(prev.map((n) => n._id));
          const merged = [...prev, ...incoming.filter((n) => !seen.has(n._id))];
          return merged;
        });
      }
    };

    const newSocket = io(HOST_API, {
      // Explicit reconnection so we recover from proxy idle-drops / brief
      // network hiccups. Every successful (re)connect also re-fetches from
      // REST to close the race window where backend emits could land
      // between disconnect and re-joining the room.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      // Join scoped rooms.
      newSocket.emit('joinUserChannel', String(user._id));
      if (user.organization) {
        newSocket.emit('joinOrgChannel', String(user.organization));
      }
      if (user.jobRoles) {
        for (const group of user.jobRoles) {
          newSocket.emit('joinGroup', String(group));
        }
      }
      // Always resync from REST after (re)connect. Catches anything the
      // socket missed during the disconnect window.
      fetchNotifications().catch(() => {
        /* swallow: next reconnect or manual refresh will retry */
      });
    });

    newSocket.on('newNotification', (data: Notification) => {
      setNotifications((prev) =>
        prev.some((n) => n._id === data._id) ? prev : [...prev, data],
      );
    });

    newSocket.on('modelUpdate', (data: ModelUpdateData) => {
      handleRealTimeUpdateRef.current(data, user);
    });

    return () => {
      newSocket.removeAllListeners();
      newSocket.close();
    };
    // Deps intentionally keyed on user identity only. The auth polling
    // updates the `user` object reference silently every ~60s; using the
    // whole object would tear down / rebuild the socket every tick and drop
    // any notifications emitted during the reconnect window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const providerValue = useMemo(
    () => ({ notifications, socket, setNotifications }),
    [notifications, socket]
  );

  return (
    <NotificationContext.Provider value={providerValue}>{children}</NotificationContext.Provider>
  );
};

export default NotificationProvider;
